import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { PasswordField } from "@/components/PasswordField";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwStrength, setPwStrength] = useState({ score: 0, valid: false, length: 0 });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const confirmOk = confirmPassword.length > 0 && confirmPassword === password;
  const canSubmit = pwStrength.valid && confirmOk && token && !loading;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/giris", { replace: true }), 1500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50" data-testid="reset-page">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-xl bg-[#1E3A8A] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-bold text-lg tracking-tight">SuperZeka</span>
        </Link>

        <div className="rounded-2xl border border-zinc-200 bg-white p-8">
          {done ? (
            <div className="text-center">
              <CheckCircle2 className="w-10 h-10 text-[#10B981] mx-auto" />
              <h1 className="mt-4 font-display text-xl font-bold text-zinc-900">Şifren güncellendi</h1>
              <p className="mt-2 text-sm text-zinc-600">Yönlendiriliyorsun…</p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">Yeni Şifre</h1>
              <p className="mt-2 text-sm text-zinc-600">Yeni şifreni belirle.</p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <PasswordField
                  id="pw"
                  label="Yeni şifre"
                  value={password}
                  onChange={setPassword}
                  onStrengthChange={setPwStrength}
                  onGenerated={(pw) => setConfirmPassword(pw)}
                  testIdPrefix="reset-password"
                />
                <div className="space-y-1.5">
                  <Label htmlFor="pw-confirm">Şifre (tekrar)</Label>
                  <Input
                    id="pw-confirm"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="rounded-xl"
                    placeholder="Şifreyi tekrar yaz"
                    autoComplete="new-password"
                    spellCheck={false}
                    data-testid="reset-password-confirm"
                  />
                  {confirmPassword.length > 0 && confirmOk && (
                    <div className="text-xs text-emerald-600 flex items-center gap-1" data-testid="reset-password-match">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                        <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
                      </svg>
                      Şifreler eşleşiyor
                    </div>
                  )}
                  {confirmPassword.length > 0 && !confirmOk && (
                    <div className="text-xs text-red-600" data-testid="reset-password-mismatch">
                      Şifreler eşleşmiyor
                    </div>
                  )}
                </div>
                {err && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {err}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="reset-submit"
                  className="w-full h-11 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Şifreyi Güncelle"}
                </Button>
                {!token && (
                  <div className="text-xs text-red-600">Bağlantıda geçerli bir token yok.</div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
